import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import authService from '../../services/authService';

const ForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.light};
`;

const ForgotPasswordCard = styled(Card)`
  max-width: 400px;
  width: 100%;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
  
  h1 {
    font-size: ${theme.fontSizes.xxlarge};
    margin-bottom: ${theme.spacing.xs};
  }
  
  p {
    color: ${theme.colors.gray};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.medium};
  font-weight: 600;
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.gray};
  border-radius: ${theme.radii.small};
  font-size: ${theme.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.danger};
  font-size: ${theme.fontSizes.small};
  margin: ${theme.spacing.xs} 0 0 0;
`;

const SuccessMessage = styled.p`
  color: ${theme.colors.success};
  font-size: ${theme.fontSizes.medium};
  text-align: center;
  margin: ${theme.spacing.md} 0;
`;

const BackToLogin = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.small};
  
  a {
    color: ${theme.colors.primary};
    font-weight: 600;
    margin-left: ${theme.spacing.xs};
  }
`;

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      
      await authService.resetPassword(email);
      
      setSubmitted(true);
    } catch (err: any) {
      console.error('Forgot password error:', err);
      const errorMessage = err.response?.data?.error || 
                          err.message || 
                          'Failed to send password reset request. Please try again later.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ForgotPasswordContainer>
      <ForgotPasswordCard>
        <Header>
          <h1>Reset Password</h1>
          <p>Enter your email to receive a password reset link</p>
        </Header>
        
        {submitted ? (
          <>
            <SuccessMessage>
              If an account exists with this email, a password reset link will be sent.
            </SuccessMessage>
            <BackToLogin>
              <Link to="/login">Back to Login</Link>
            </BackToLogin>
          </>
        ) : (
          <>
            <Form onSubmit={handleSubmit}>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              
              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                />
              </FormGroup>
              
              <Button type="submit" fullWidth disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </Form>
            
            <BackToLogin>
              Remember your password?
              <Link to="/login">Sign In</Link>
            </BackToLogin>
          </>
        )}
      </ForgotPasswordCard>
    </ForgotPasswordContainer>
  );
};

export default ForgotPassword; 